import { useSSEEvent } from '@/hooks/use-event-stream'
import type { DocumentStatus } from '@/types'
import { useCallback, useEffect, useRef, useState } from 'react'

interface ProcessingProgress {
  documentId: number
  status: DocumentStatus
  progress: number // 0-100
  statusText: string
  filename?: string
}

/**
 * Tracks progress for documents that are currently processing.
 * Listens for SSE events to detect status changes.
 * Uses simulated progress animation for smooth UX while waiting.
 */
export function useDocumentProcessingProgress(
  documents: Array<{ id: number; status: string; original_filename: string }> | undefined,
) {
  const [progressMap, setProgressMap] = useState<Record<number, ProcessingProgress>>({})
  const intervalsRef = useRef<Record<number, ReturnType<typeof setInterval>>>({})
  const targetProgressRef = useRef<Record<number, number>>({})

  const startTracking = useCallback((docId: number, filename: string) => {
    // Initialize progress
    setProgressMap((prev) => ({
      ...prev,
      [docId]: {
        documentId: docId,
        status: 'processing',
        progress: 5,
        statusText: 'Processing your resume...',
        filename,
      },
    }))

    // Smooth animation: interpolates toward the target (set by SSE events)
    // Falls back to slow simulation if no SSE progress arrives
    const progressInterval = setInterval(() => {
      setProgressMap((prev) => {
        const current = prev[docId]
        if (!current || current.status !== 'processing') return prev
        const target = targetProgressRef.current[docId] ?? 0
        const p = current.progress

        let newProgress: number
        if (target > p) {
          // Animate toward real backend progress
          newProgress = Math.min(target, p + Math.max(0.5, (target - p) * 0.15))
        } else {
          // Slow crawl when no new target (backend hasn't reported yet)
          const increment = p < 15 ? 0.8 : 0.1
          newProgress = Math.min(90, p + increment)
        }
        return { ...prev, [docId]: { ...current, progress: newProgress } }
      })
    }, 500)
    intervalsRef.current[docId] = progressInterval
  }, [])

  const completeTracking = useCallback((docId: number, status: 'ready' | 'failed') => {
    // Clear animation interval
    if (intervalsRef.current[docId]) {
      clearInterval(intervalsRef.current[docId])
      delete intervalsRef.current[docId]
    }

    if (status === 'ready') {
      setProgressMap((prev) => {
        if (!prev[docId]) return prev
        return {
          ...prev,
          [docId]: { ...(prev[docId] as ProcessingProgress), status: 'ready', progress: 100 },
        }
      })
      // Remove from progress map after animation completes
      setTimeout(() => {
        setProgressMap((prev) => {
          const next = { ...prev }
          delete next[docId]
          return next
        })
      }, 1500)
    } else {
      setProgressMap((prev) => {
        if (!prev[docId]) return prev
        return {
          ...prev,
          [docId]: { ...(prev[docId] as ProcessingProgress), status: 'failed', progress: 0 },
        }
      })
      // Remove failed item from progress map after a short delay
      setTimeout(() => {
        setProgressMap((prev) => {
          const next = { ...prev }
          delete next[docId]
          return next
        })
      }, 3000)
    }
  }, [])

  // Listen for SSE document status changes
  useSSEEvent('document_status_changed', (event) => {
    const data = event.data as { document_id: number; status: string }
    if (data.status === 'ready' || data.status === 'failed' || data.status === 'invalid_document') {
      completeTracking(data.document_id, data.status === 'ready' ? 'ready' : 'failed')
      delete targetProgressRef.current[data.document_id]
    }
  })

  // Listen for real backend processing progress
  useSSEEvent('document_processing_progress', (event) => {
    const data = event.data as { document_id: number; progress: number; status_text?: string }
    if (data.progress != null) {
      targetProgressRef.current[data.document_id] = data.progress
    }
    if (data.status_text != null) {
      setProgressMap((prev) => {
        const current = prev[data.document_id]
        if (!current) return prev
        return {
          ...prev,
          [data.document_id]: { ...current, statusText: data.status_text as string },
        }
      })
    }
  })

  // Track any new processing documents from the documents list
  useEffect(() => {
    if (!documents) return
    const processing = documents.filter(
      (d) => d.status === 'processing' || d.status === 'uploading',
    )
    for (const doc of processing) {
      if (!progressMap[doc.id] && !intervalsRef.current[doc.id]) {
        startTracking(doc.id, doc.original_filename)
      }
    }
  }, [documents, progressMap, startTracking])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      for (const id of Object.keys(intervalsRef.current)) {
        clearInterval(intervalsRef.current[Number(id)])
      }
    }
  }, [])

  const processingItems = Object.values(progressMap)
  const hasProcessing = processingItems.length > 0

  return { progressMap, processingItems, hasProcessing }
}
